import type { Currency, Transaction } from '@/types/transaction'

// Kept here rather than in download-transactions.tsx so the Cypress spec asserting the
// cap can import it without pulling the component (and its aliased imports) into the bundle
export const MAX_EXPORTED_TRANSACTIONS = 30000

// Can use navigator.language as well but likely set to en-US instead of en-CA
// for Canadians in browsers by default, and want to avoid show "CA" in front of price
export const getLocale = (currency: Currency) => `en-${currency?.slice(0, -1) ?? 'US'}`

// Partial unknown-items review: identified cart + average-basket top-up. The sale and
// override legs are both real operator payout (charge success pays both buckets; charge
// failure raises the override to cover both). Distinct from PAYMENT_ISSUE / CHARGE_FAILED,
// where summing would double-count because the override already is the full protect amount.
const UNKNOWN_ITEMS_TAKEN = 'UNKNOWN_ITEMS_TAKEN'

const UNKNOWN_ITEMS_TAKEN_REASONS = [
	UNKNOWN_ITEMS_TAKEN,
	'UNKNOWN_ITEMS_TAKEN_NO_PAYOUT',
	'UNKNOWN_ITEMS_TAKEN_CHARGE_FAILED',
]

const ITEMS_TAKEN_UNKNOWN_DECISION = 'items_taken_unknown'

// Reviewer concluded something was taken but could not identify any of it: a $0 order with
// no items where the customer was not charged. The override reason covers orders resolved
// before fridge_open.review_decision existed.
export function isItemsNotDetermined(
	transaction: Pick<Transaction, 'order_items' | 'payout_override_reason' | 'fridge_open'>
): boolean {
	if (transaction.order_items?.length) {
		return false
	}
	return (
		transaction.fridge_open?.review_decision === ITEMS_TAKEN_UNKNOWN_DECISION ||
		UNKNOWN_ITEMS_TAKEN_REASONS.includes(transaction.payout_override_reason ?? '')
	)
}

export function isCoveredByRevenueProtect(transaction: Pick<Transaction, 'payout_override_amount'>): boolean {
	return (transaction.payout_override_amount ?? 0) > 0
}

// Gross Revenue Protect amount credited to the operator, before the override fee.
export function getRevenueProtectCredit(transaction: Pick<Transaction, 'payout_override_total'>): number {
	return transaction.payout_override_total ?? 0
}

export function isPartialUnknownItemsTopUp(transaction: Pick<Transaction, 'total' | 'payout_override_reason' | 'payout_override_amount'>): boolean {
	return (
		(transaction.total ?? 0) > 0 &&
		transaction.payout_override_reason === UNKNOWN_ITEMS_TAKEN &&
		(transaction.payout_override_amount ?? 0) > 0
	)
}

export function getTransactionPayoutAmount(transaction: Pick<Transaction, 'total' | 'payout_amount' | 'payout_override_amount' | 'payout_override_reason'>): number {
	if (isPartialUnknownItemsTopUp(transaction)) {
		return (transaction.payout_amount ?? 0) + (transaction.payout_override_amount ?? 0)
	}
	return transaction.payout_override_amount ?? transaction.payout_amount ?? 0
}

export function getTransactionFeeAmount(transaction: Pick<Transaction, 'total' | 'fee' | 'payout_override_fee' | 'payout_override_reason' | 'payout_override_amount'>): number {
	if (isPartialUnknownItemsTopUp(transaction)) {
		return (transaction.fee ?? 0) + (transaction.payout_override_fee ?? 0)
	}
	return transaction.payout_override_fee ?? transaction.fee ?? 0
}

// Distinct cabinet names across the taps of a multi-cabinet session order.
export function getSessionCabinetNames(transaction: Pick<Transaction, 'fridge_opens'>): string[] {
	const names = (transaction.fridge_opens ?? [])
		.map((open) => open.site?.name)
		.filter((name): name is string => Boolean(name))
	return [...new Set(names)]
}

// Cabinet each line came from, keyed by tap; empty unless the taps span several cabinets.
export function getCabinetNameByFridgeOpen(transaction: Pick<Transaction, 'fridge_opens'>): Map<number, string> {
	if (getSessionCabinetNames(transaction).length < 2) {
		return new Map()
	}
	return new Map(
		(transaction.fridge_opens ?? [])
			.filter((open) => open.site?.name)
			.map((open) => [open.id, open.site!.name])
	)
}

// The order's total isn't final until every tap is reviewed. `fridge_open` is a single,
// arbitrary tap of a multi-cabinet session order, so prefer the full list when loaded.
export function isAwaitingReview(transaction: Pick<Transaction, 'paid_at' | 'charge_failed_at' | 'fridge_open' | 'fridge_opens'>): boolean {
	if (transaction.paid_at || transaction.charge_failed_at) {
		return false
	}
	if (transaction.fridge_opens?.length) {
		return transaction.fridge_opens.some((open) => !open.reviewed_at)
	}
	return !transaction.fridge_open?.reviewed_at
}

export function getRevenueProtectLabel(transaction: Pick<Transaction, 'total' | 'payout_override_amount' | 'payout_override_reason'>): {
	reason: string
	tooltip: string
} | null {
	if (!transaction.payout_override_amount) {
		return null
	}

	if (isPartialUnknownItemsTopUp(transaction)) {
		return {
			reason: 'Includes Revenue Protect',
			tooltip:
				'Part of this payout is a Revenue Protect top-up for items that could not be identified. The rest is the identified cart.',
		}
	}

	return {
		reason: 'Covered by Revenue Protect',
		tooltip:
			"Revenue Protect compensates for certain transactions that couldn't be settled, such as declined adjustments or technical issues. This amount is included in Gross Sales.",
	}
}
